import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDropdowns, useAssignable } from "@/hooks/useMeta";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const MANAGER_ROLES = ["sales_head", "region_head", "sales_manager", "salesperson"];

export function UniverseCompanyDialog({ open, onOpenChange, initial, onSaved, skipDupGate }) {
  const { user } = useAuth();
  const { data: dd } = useDropdowns();
  const { data: allUsers = [] } = useAssignable();
  const managers = allUsers.filter((u) => MANAGER_ROLES.includes(u.role)).sort((a, b) => a.name.localeCompare(b.name));
  const canAssign = ["super_admin", "sales_head", "region_head", "sales_manager"].includes(user?.role);

  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  const [matches, setMatches] = useState([]);
  const [confirmDup, setConfirmDup] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const isEdit = !!initial?.id;
  const segments = dd?.universe_segment || ["Hospitality", "Education", "Government", "CRE", "Other"];
  const segmentIsOther = f.segment === "Other";
  const dealerClass = ["B+", "B", "C"].includes(f.category) && user?.role !== "dealer";

  const seeded = useRef(false);
  useEffect(() => {
    if (!open) { seeded.current = false; return; }
    if (seeded.current) return;
    seeded.current = true;
    const init = { ...(initial || {}) };
    if (init.segment && !segments.includes(init.segment)) {
      init.segment_custom = init.segment;
      init.segment = "Other";
    }
    setF(init);
    setConfirmDup(false);
    setMatches([]);
  }, [open, initial]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: dealers = [] } = useQuery({
    queryKey: ["dealers-zone", f.dealer_zone],
    queryFn: async () => (await api.get("/dealers", { params: { zone: f.dealer_zone } })).data,
    enabled: open && dealerClass && !!f.dealer_zone,
  });
  const dealerList = Array.isArray(dealers) ? dealers : dealers?.items || [];

  useEffect(() => {
    setConfirmDup(false);
    if (isEdit || !open || !(f.company || "").trim() || f.company.trim().length < 3) { setMatches([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/master-data/match", { params: { q: f.company } });
        setMatches((data || []).filter((m) => m.id !== initial?.id));
      } catch { setMatches([]); }
    }, 450);
    return () => clearTimeout(t);
  }, [f.company, open, isEdit]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!f.company?.trim()) { toast.error("Company name is required"); return; }
    if (dealerClass && !f.dealer_id) { toast.error(`Class ${f.category} accounts are dealer-serviced — pick the zone and assign a dealer`); return; }
    if (!skipDupGate && !isEdit && matches.length > 0 && !confirmDup) {
      setConfirmDup(true);
      toast.warning("This company may already be in the Universe — review the matches shown, then press “Add Anyway” if it's genuinely different.");
      return;
    }
    setSaving(true);
    const payload = { ...f, segment: segmentIsOther && f.segment_custom?.trim() ? f.segment_custom.trim() : f.segment };
    delete payload.segment_custom;
    if (!dealerClass) { payload.dealer_zone = ""; payload.dealer_id = ""; payload.dealer_name = ""; }
    try {
      let doc;
      if (isEdit) doc = (await api.put(`/master-data/${initial.id}`, payload)).data;
      else doc = (await api.post("/master-data", payload)).data;
      toast.success(isEdit ? "Company updated" : "Company added to the Universe");
      onOpenChange(false); onSaved?.(doc);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const DD = ({ k, label, options }) => (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-slate-700">{label}</Label>
      <Select value={f[k] || ""} onValueChange={(v) => set(k, v === "__none" ? "" : v)}>
        <SelectTrigger className="h-9" data-testid={`uni-${k}`}><SelectValue placeholder="Select" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__none">— None —</SelectItem>
          {(options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="universe-form-dialog">
        <DialogHeader><DialogTitle className="font-heading">{isEdit ? "Edit Company" : "Add Company to Universe"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs font-medium text-slate-700">Company Name <span className="text-red-500">*</span></Label>
            <Input value={f.company || ""} onChange={(e) => set("company", e.target.value)} className="h-9" data-testid="uni-company" />
            {!isEdit && matches.length > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5 space-y-1.5" data-testid="universe-dup-warning">
                <p className="text-[11px] font-semibold text-amber-800 flex items-center gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5" /> Already in the Universe — check before adding a duplicate:
                </p>
                {matches.slice(0, 4).map((m) => (
                  <p key={m.id} className="text-xs text-amber-900 pl-5" data-testid={`universe-dup-match-${m.id}`}>
                    <span className={m.company.toLowerCase() === (f.company || "").trim().toLowerCase() ? "font-bold" : "font-medium"}>{m.company}</span>
                    {m.location ? ` · ${m.location}` : " · no HQ set"}{m.type ? ` · ${m.type}` : ""}{m.category ? ` · Class ${m.category}` : ""}
                  </p>
                ))}
                <p className="text-[10px] text-amber-700 pl-5">If the details match one of these, close this form and use the existing entry instead.</p>
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-700">Headquarter</Label>
            <Input value={f.location || ""} onChange={(e) => set("location", e.target.value)} className="h-9" placeholder="e.g. Mumbai" data-testid="uni-location" />
          </div>
          <DD k="type" label="Type" options={dd?.universe_type} />
          <DD k="category" label="Class" options={dd?.universe_class} />
          {dealerClass && (
            <div className="sm:col-span-2 rounded-md border border-indigo-100 bg-indigo-50/50 p-3 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="uni-dealer-block">
              <p className="sm:col-span-2 text-[11px] font-semibold uppercase tracking-wider text-indigo-500">Class {f.category} — dealer-serviced account · assign a dealer</p>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-700">Zone</Label>
                <Select value={f.dealer_zone || ""} onValueChange={(v) => setF((x) => ({ ...x, dealer_zone: v, dealer_id: "", dealer_name: "" }))}>
                  <SelectTrigger className="h-9" data-testid="uni-dealer-zone"><SelectValue placeholder="Select zone" /></SelectTrigger>
                  <SelectContent>{["North", "South", "West", "Exports"].map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-700">Dealer</Label>
                <Select value={f.dealer_id || ""} onValueChange={(v) => { const d = dealerList.find((x) => x.id === v); setF((x) => ({ ...x, dealer_id: v, dealer_name: d?.name || d?.company || "" })); }} disabled={!f.dealer_zone}>
                  <SelectTrigger className="h-9" data-testid="uni-dealer-select"><SelectValue placeholder={f.dealer_zone ? "Select dealer" : "Pick zone first"} /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {dealerList.map((d) => <SelectItem key={d.id} value={d.id}>{d.name || d.company}</SelectItem>)}
                    {dealerList.length === 0 && <SelectItem value="__none" disabled>No dealers in this zone</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DD k="segment" label="Segment" options={segments} />
          {segmentIsOther && (
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-700">Custom Segment</Label>
              <Input value={f.segment_custom || ""} onChange={(e) => set("segment_custom", e.target.value)} className="h-9" placeholder="Enter segment" data-testid="uni-segment-custom" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-700">Contact Person</Label>
            <Input value={f.contact_person || ""} onChange={(e) => set("contact_person", e.target.value)} className="h-9" data-testid="uni-contact_person" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-700">Mobile Number <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Input value={f.contact_number || ""} onChange={(e) => set("contact_number", e.target.value)} className="h-9" data-testid="uni-contact_number" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-700">Email ID</Label>
            <Input value={f.email || ""} onChange={(e) => set("email", e.target.value)} className="h-9" data-testid="uni-email" />
          </div>
          {canAssign && (
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-700">Sales Manager</Label>
              <Select value={f.sales_manager_id || ""} onValueChange={(v) => set("sales_manager_id", v === "__none" ? "" : v)}>
                <SelectTrigger className="h-9" data-testid="uni-sales_manager"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  <SelectItem value="__none">— None —</SelectItem>
                  {managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="uni-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className={`gap-2 ${confirmDup && !isEdit ? "bg-amber-600 hover:bg-amber-700" : "bg-slate-900 hover:bg-slate-800"}`} data-testid="uni-save">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}{isEdit ? "Save Changes" : confirmDup ? "Add Anyway" : "Add Company"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
